import { useCallback } from 'react';

import useRequestJSON, { RunnerSegment } from './hooks/useRequestJSON';
import {
  PostJSON,
  BaseResponse,
  PaginatedParams,
  ErrorResponse
} from './request';
import { IPatient } from './patient';
import { IContact } from './contact';
import { IUser } from './user';
import { IBrand } from './brand';
import { PatientGroupFilters } from './group';

/**
 * Thread
 */

export interface IChatThread {
  id: number;
  lastMessageAt?: Date;
  lastPingAt?: Date;
  lastReadByPatientAt?: Date;
  lastReadByUserAt?: Date;
  patientId: number;
  brandId: number;
  userId?: number;
  accountId: number;
  patient: IPatient;
  brand: IBrand;
  contact?: IContact;
  contactId?: number;
  isOptedOut: boolean;
  // Labels
  numUnread?: number;
  // Last care manager message for read receipts
  lastCMMessage?: {
    id: number;
    isRead: boolean;
    readAt?: Date;
    createdAt: Date;
    threadId: number;
  };
  // State
  isSecure?: boolean;
  isInHours?: boolean;
}

export interface GetChatThreadsResponse extends BaseResponse {
  total: number;
  threads: IChatThread[];
}

export interface GetChatThreadsResult {
  total: number;
  threads: IChatThread[];
  setThreads: (threads: IChatThread[]) => void;
  isLoading: boolean;
}

interface GroupFilters {
  filters: PatientGroupFilters;
}

interface NameFilter {
  name?: string;
}

interface PatientIdFilter {
  patientIds?: number[];
}

export type GetChatThreadsParams = PaginatedParams &
  GroupFilters &
  NameFilter &
  PatientIdFilter;

export const useGetChatThreads = (
  params: GetChatThreadsParams,
  isEnabled?: boolean
): GetChatThreadsResult & RunnerSegment => {
  const request = useCallback(
    () =>
      PostJSON({
        url: '/chat/data_threads/',
        params
      }),
    [params.page, params.limit, params.filters]
  );
  const { data, setData, isLoading, run } =
    useRequestJSON<GetChatThreadsResponse>({
      request,
      enabled: isEnabled
    });
  return {
    total: data?.total || 0,
    threads: data?.threads || [],
    setThreads: (threads) => {
      setData({
        ...data,
        threads
      });
    },
    isLoading: !isEnabled ? false : isLoading,
    run: () => run(request)
  };
};

/**
 * Message
 */

export enum SystemMessageType {
  MakeSecure = 'make_secure',
  MakeInsecure = 'make_insecure',
  PatientJoin = 'patient_join'
}

export interface IChatMessage {
  id: number;
  body: string;
  isSecure: boolean;
  isByPatient: boolean;
  isRead: boolean;
  readAt?: Date;
  createdAt: Date;
  patientId: number;
  userId?: number;
  threadId: number;
  patient?: IPatient;
  user?: IUser;
  // Pseudo flags
  isSystem?: boolean;
  systemMessageType?: SystemMessageType;
}

export interface GetChatMessagesParams {
  patientId: number;
  cutoff?: string;
}

export interface GetChatMessagesResponse extends BaseResponse {
  messages: IChatMessage[];
  cutoff: string;
}

export interface GetChatMessagesResult {
  total: number;
  messages: IChatMessage[];
  setMessages: (messages: IChatMessage[]) => void;
  isLoading: boolean;
}

export interface EnsureThreadParams {
  patientId: number;
  brandId: number;
}

export interface EnsureThreadResponse extends BaseResponse {
  thread: IChatThread;
}

export const EnsureThread = (
  params: EnsureThreadParams
): Promise<EnsureThreadResponse | ErrorResponse> => {
  return PostJSON<EnsureThreadResponse>({
    url: '/chat/ensure_thread/',
    params
  });
};

export interface MarkThreadAsReadParams {
  patientId: number;
  brandId: number;
}

export interface MarkThreadAsReadResponse extends BaseResponse {
  thread: IChatThread;
}

export const MarkThreadAsRead = (
  params: MarkThreadAsReadParams
): Promise<MarkThreadAsReadResponse | ErrorResponse> => {
  return PostJSON<MarkThreadAsReadResponse>({
    url: '/chat/mark_as_read/',
    params
  });
};

export interface VerifyTokenParams {
  token: string;
}

export interface VerifyTokenResponse extends BaseResponse {
  patient: Partial<IPatient>;
  threads: IChatThread[];
}

export const VerifyToken = (
  params: VerifyTokenParams
): Promise<VerifyTokenResponse | ErrorResponse> => {
  return PostJSON<VerifyTokenResponse>({
    url: '/chat/verify/',
    params
  });
};

export interface GetThreadParams {
  patientId: number;
}

export interface GetThreadResponse extends BaseResponse {
  thread: IChatThread;
}

export const GetThread = (
  params: GetThreadParams
): Promise<GetThreadResponse | ErrorResponse> => {
  return PostJSON<GetThreadResponse>({
    url: '/chat/data_thread/',
    params
  });
};

export interface EditAssociatedBrandParams {
  patientId: number;
  brandId: number;
}

export const EditAssociatedBrand = (
  params: EditAssociatedBrandParams
): Promise<GetThreadResponse | ErrorResponse> => {
  return PostJSON<GetThreadResponse>({
    url: '/chat/edit_associated_brand/',
    params
  });
};

export interface EditThreadContactIdParams {
  patientId: number;
  contactId: number;
}

export const EditThreadContactId = (
  params: EditThreadContactIdParams
): Promise<GetThreadResponse | ErrorResponse> => {
  return PostJSON<GetThreadResponse>({
    url: '/chat/edit_thread_contact_id/',
    params
  });
};

export interface MarkThreadUnreadParams {
  patientId: number;
  previousLastReadAt: Date;
  previousNumUnread: number;
}

export interface MarkThreadUnreadResponse extends BaseResponse {
  thread: IChatThread;
}

export const MarkThreadUnread = (
  params: MarkThreadUnreadParams
): Promise<MarkThreadUnreadResponse | ErrorResponse> => {
  return PostJSON<MarkThreadUnreadResponse>({
    url: '/chat/mark_as_unread/',
    params
  });
};

export interface MarkThreadAsReadPatientParams {
  token: string;
  threadId: number;
}

export interface MarkThreadAsReadPatientResponse extends BaseResponse {
  message: string;
}

export const MarkThreadAsReadPatient = (
  params: MarkThreadAsReadPatientParams
): Promise<MarkThreadAsReadPatientResponse | ErrorResponse> => {
  return PostJSON<MarkThreadAsReadPatientResponse>({
    url: '/chat/mark_read/',
    params
  });
};

export interface IChatEvent {
  id: number;
  type: string;
  createdAt: Date;
  description: string;
  user?: IUser;
}

export interface GetChatEventsParams extends PaginatedParams {
  patientId: number;
}

export interface GetChatEventsResponse extends BaseResponse {
  events: IChatEvent[];
  total: number;
}

export const GetChatEvents = (
  params: GetChatEventsParams
): Promise<GetChatEventsResponse | ErrorResponse> => {
  return PostJSON<GetChatEventsResponse>({
    url: '/chat/data_events/',
    params
  });
};
